/**
 * Componente para exibir grade de jobs/tasks ativos da empresa
 * 
 * @author Equipe GestaoeCRM
 * @date 02/10/2025
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getActiveOS } from '@/services/osApi';
import { useAuth } from '@/contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';
import { Clock, User, Play, Pause, CheckCircle } from 'lucide-react';

interface ActiveOS {
  id: string;
  orderOfServiceId: string;
  orderOfService: {
    id: string;
    title: string;
    type: string;
    priority: string;
    estimatedHours: number;
    actualHours: number;
    startDate: string;
    demand: {
      id: string;
      title: string;
      project: {
        name: string;
        client: {
          name: string;
        };
      } | null;
    };
  };
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  startTime: string;
  lastHeartbeat: string;
  isActive: boolean;
  currentDuration: number;
  lastHeartbeatMinutes: number;
}

export default function CompanyActiveOSGrid() {
  const { user } = useAuth();
  const [localActiveOS, setLocalActiveOS] = useState<ActiveOS[]>([]);

  // Buscar jobs/tasks ativos da empresa
  const { data: activeOSData, isLoading, error } = useQuery({
    queryKey: ['company-active-os'],
    queryFn: getActiveOS,
    enabled: !!user && (user.role === 'ADMIN' || user.role === 'GESTOR'),
    refetchInterval: 30000, // Atualizar dados do servidor a cada 30 segundos
    staleTime: 0, // Sempre considerar dados como stale para forçar atualização
  });

  // Atualizar dados locais quando dados do servidor mudarem
  useEffect(() => {
    // console.log('🔄 CompanyActiveOSGrid: Dados do servidor atualizados:', {
    // hasData: !!activeOSData?.data,
    // dataLength: activeOSData?.data?.length || 0,
    // data: activeOSData?.data,
    // timestamp: new Date().toISOString()
    // });
    
    if (activeOSData?.data) {
      setLocalActiveOS(activeOSData.data);
      // console.log('✅ CompanyActiveOSGrid: Dados locais atualizados com', activeOSData.data.length, 'OS ativas');
    }
  }, [activeOSData]);

  // Atualizar duração e heartbeat a cada segundo
  useEffect(() => {
    const interval = setInterval(() => {
      setLocalActiveOS(prevOS => 
        prevOS.map(os => {
          const now = new Date();
          const startTime = new Date(os.startTime);
          const lastHeartbeat = new Date(os.lastHeartbeat);
          
          return {
            ...os,
            currentDuration: Math.round(((now.getTime() - startTime.getTime()) / (1000 * 60 * 60)) * 100) / 100,
            lastHeartbeatMinutes: Math.round(((now.getTime() - lastHeartbeat.getTime()) / (1000 * 60)) * 100) / 100
          };
        })
      );
    }, 1000); // Atualizar a cada segundo

    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Erro ao carregar jobs/tasks ativos</p>
      </div>
    );
  }

  const activeOS: ActiveOS[] = localActiveOS;

  const getStatusIcon = (isActive: boolean) => {
    if (isActive) {
      return <Play className="h-4 w-4 text-green-600" />;
    }
    return <Clock className="h-4 w-4 text-gray-600" />;
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Ativa' : 'Inativa';
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  const formatDuration = (hours: number) => {
    const totalMinutes = Math.floor(hours * 60);
    const diffHours = Math.floor(totalMinutes / 60);
    const diffMinutes = totalMinutes % 60;
    
    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    }
    return `${diffMinutes}m`;
  };

  if (activeOS.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum job/task ativo</h3>
        <p className="mt-1 text-sm text-gray-500">
          Não há jobs/tasks em andamento no momento.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Cabeçalho da Grade */}
      <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
        <div className="grid grid-cols-12 gap-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
          <div className="col-span-3">OS / Funcionário</div>
          <div className="col-span-2">Tipo / Demanda</div>
          <div className="col-span-2">Projeto / Cliente</div>
          <div className="col-span-2">Status / Duração</div>
          <div className="col-span-2">Horas</div>
          <div className="col-span-1">Heartbeat</div>
        </div>
      </div>

      {/* Corpo da Grade */}
      <div className="bg-white divide-y divide-gray-200">
        {activeOS.map((os) => (
          <div key={os.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
            <div className="grid grid-cols-12 gap-4 items-center">
              {/* OS / Funcionário */}
              <div className="col-span-3">
                <div className="flex items-center space-x-1">
                  {getStatusIcon(os.isActive)}
                  <div className="min-w-0 flex-1">
                    <h4 className="text-sm font-medium text-gray-900 truncate">
                      {os.orderOfService.title}
                    </h4>
                    <div className="flex items-center space-x-1 mt-1">
                      <User className="h-3 w-3 text-gray-400" />
                      <span className="text-xs text-gray-600">{os.user.name}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tipo / Demanda */}
              <div className="col-span-2">
                <div className="text-sm text-gray-900">{os.orderOfService.type}</div>
                {os.orderOfService.demand && (
                  <div className="text-xs text-gray-500 truncate">
                    {os.orderOfService.demand.title}
                  </div>
                )}
              </div>

              {/* Projeto / Cliente */}
              <div className="col-span-2">
                {os.orderOfService.demand?.project ? (
                  <>
                    <div className="text-sm text-gray-900 truncate">
                      {os.orderOfService.demand.project.name}
                    </div>
                    {os.orderOfService.demand.project.client && (
                      <div className="text-xs text-gray-500 truncate">
                        {os.orderOfService.demand.project.client.name}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-gray-400">-</div>
                )}
              </div>

              {/* Status / Duração */}
              <div className="col-span-2">
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(os.isActive)}`}>
                    {getStatusText(os.isActive)}
                  </span>
                  {os.isActive && (
                    <div className="text-sm font-mono text-green-700">
                      {formatDuration(os.currentDuration)}
                    </div>
                  )}
                </div>
                {os.startTime && (
                  <div className="text-xs text-gray-500 mt-1">
                    {new Date(os.startTime).toLocaleString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                )}
              </div>

              {/* Horas */}
              <div className="col-span-2">
                <div className="text-sm text-gray-900">
                  {os.orderOfService.actualHours.toFixed(2)}h
                </div>
                {os.orderOfService.estimatedHours && (
                  <div className="text-xs text-gray-500">
                    / {os.orderOfService.estimatedHours}h
                  </div>
                )}
              </div>

              {/* Heartbeat */}
              <div className="col-span-1">
                <div className="flex items-center space-x-1">
                  {os.isActive && (
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  )}
                  <span className="text-xs text-gray-500">
                    {os.lastHeartbeatMinutes.toFixed(1)}min
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
